import FVector from '@data/geometry/FVector';
import CustomCamera, { CameraSettings } from './CustomCamera';
import * as THREE from 'three';
import { VisualizationElements } from 'Graphics/VisualizationElements';
import usePersistentStore, { getGlobalState } from '@gui/store/persistent/persistentStore';
import { Panels } from '@gui/constants/Panels';

export class CameraPov extends CustomCamera {
    // Camera state

    /**
     * Cached position of the previous camera mode. It's used to return it when deactivating the POV camera and switching back
     */
    private cachedPosition = new FVector();

    /**
     * Cached target of the previous camera mode. It's used to return it when deactivating the POV camera and switching back
     */
    private cachedTarget = new FVector();

    private cachedOldShowGridValue?: boolean;

    public setReturnPose(position: FVector, target: FVector): void {
        this.cachedPosition = position;
        this.cachedTarget = target;
    }

    public override activate(position: FVector, target: FVector): void {
        this.updateControlsSettings();
        this.cachedOldShowGridValue = getGlobalState().context.showGrid;
        usePersistentStore.getState().setPanel({ showGrid: false }, Panels.CONTEXT);
        if (VisualizationElements.selectionBox) VisualizationElements.selectionBox.visible = false;

        super.setPositionAndTarget(position, target);
    }
    public override deActivate(): { position: FVector; target: FVector } {
        if (VisualizationElements.selectionBox) VisualizationElements.selectionBox.visible = true;
        usePersistentStore.getState().setPanel({ showGrid: this.cachedOldShowGridValue }, Panels.CONTEXT);
        return { position: this.cachedPosition, target: this.cachedTarget };
    }

    public override setPositionAndTarget(position: FVector, direction: FVector): void {
        const target = new FVector(position.x, position.z, -position.y);

        const dir =
            direction && !FVector.isZero(direction)
                ? new FVector(direction.x, direction.z, -direction.y)
                : FVector.fromThree(this.camera.getWorldDirection(new THREE.Vector3()));
        const distance = 0.01; // adjust this for how far behind you want to be
        const cameraPosition = target.clone().sub(dir.clone().multiplyScalar(distance));
        super.setPositionAndTarget(cameraPosition, target);
    }

    protected override defaultSettings(): CameraSettings {
        return {
            ...super.defaultSettings(),
            enablePan: false,
            enableZoom: false,
            trackBallZoom: false,
        };
    }

    private lockVerticalRotation() {
        this.settings.minAzimuthAngle = this.orbitControls.getAzimuthalAngle();
        this.settings.maxAzimuthAngle = this.orbitControls.getAzimuthalAngle();
    }

    private unlockVerticalRotation() {
        const { minAzimuthAngle, maxAzimuthAngle } = this.defaultSettings();
        this.settings.minAzimuthAngle = minAzimuthAngle;
        this.settings.maxAzimuthAngle = maxAzimuthAngle;
    }

    private lockHorizontalRotation() {
        this.settings.minPolarAngle = this.orbitControls.getPolarAngle();
        this.settings.maxPolarAngle = this.orbitControls.getPolarAngle();
    }

    private unlockHorizontalRotation() {
        const { minPolarAngle, maxPolarAngle } = this.defaultSettings();
        this.settings.minPolarAngle = minPolarAngle;
        this.settings.maxPolarAngle = maxPolarAngle;
    }

    public updateLocks(lockPovVerticalRotation: boolean, lockPovHorizontalRotation: boolean) {
        if (lockPovVerticalRotation) {
            this.lockVerticalRotation();
        } else {
            this.unlockVerticalRotation();
        }
        if (lockPovHorizontalRotation) {
            this.lockHorizontalRotation();
        } else {
            this.unlockHorizontalRotation();
        }

        this.updateControlsSettings();
    }

    public isLocked(): boolean {
        return (
            this.settings.minAzimuthAngle === this.settings.maxAzimuthAngle && this.settings.minPolarAngle === this.settings.maxPolarAngle
        );
    }
}
